import { SheetPageBody, SheetPageTitle } from "@/components/ui/sheet-page"
import { useNewProps } from "./page-props"
import { CirclePlus } from "lucide-react"
import * as z from "zod"
import { VALIDATION_ERROR_KEYS } from "@/i18n"
import { useController, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { FormField } from "@/components/ui/form-field"
import {
  ControlledRadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group"
import { Label } from "@/components/ui/label"
import { ControlledCombobox } from "@/components/ui/combobox"
import { useNavigate } from "@/lib/page-rendering"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { useTranslation } from "react-i18next"
import { CommandItem } from "@/components/ui/command"

const schema = z.object({
  wishlistItem: z.discriminatedUnion("_create", [
    z.object({
      wishlistId: z.uuid(VALIDATION_ERROR_KEYS.REQUIRED),
      _create: z.literal("false"),
    }),
    z.object({
      _create: z.literal("true"),
      duration: z.number(VALIDATION_ERROR_KEYS.REQUIRED),
      student: z.number(VALIDATION_ERROR_KEYS.REQUIRED),
    }),
  ]),
})
export default function Page() {
  const navigate = useNavigate()
  const { students, change_wishlists, selected_class } = useNewProps()
  const { t } = useTranslation(["change_wishlists", "common"])
  const studentOptions = students.map((student, index) => ({
    value: index,
    label: `${student.student_name}: ${student.class_name}`,
    disabled: change_wishlists.some(
      (item) => item.class_uuid === student.class_uuid,
    ),
  }))

  const wishlistItemOptions = change_wishlists.map((item) => ({
    value: item.id,
    label: `${item.student_name}: ${item.leaving_class_name}`,
    disabled:
      item.leaving_priority_uuid === selected_class.priority_uuid &&
      item.leaving_instance_index === selected_class.instance_index,
  }))

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { wishlistItem: { _create: "true" } },
  })

  const onSubmit = (data: z.infer<typeof schema>) => {
    const payload =
      data.wishlistItem._create === "true"
        ? {
            change_wishlist_attributes: {
              duration: data.wishlistItem.duration,
              leaving_priority_uuid:
                students[data.wishlistItem.student].priority_uuid,
              leaving_instance_index:
                students[data.wishlistItem.student].instance_index,
              student_id: students[data.wishlistItem.student].student_id,
              class_uuid: students[data.wishlistItem.student].class_uuid,
              details: {
                leaving_class_name:
                  students[data.wishlistItem.student].class_name,
              },
              school_id: students[data.wishlistItem.student].school_id,
              request_origin:
                students[data.wishlistItem.student].request_origin,
            },
          }
        : {
            learners_change_wishlist_id: data.wishlistItem.wishlistId,
          }
    navigate({
      href: ".",
      method: "post",
      data: {
        item: {
          ...payload,
          joining_priority_uuid: selected_class.priority_uuid,
          joining_instance_index: selected_class.instance_index,
          details: {
            joining_class_name: selected_class.class_name,
          },
        },
      },
    })
  }

  const { field: wishlistItemStudentField } = useController({
    name: "wishlistItem.student",
    control,
  })

  const { field: wishlistField } = useController({
    name: "wishlistItem.wishlistId",
    control,
  })

  return (
    <SheetPageBody>
      <SheetPageTitle icon={CirclePlus}>{t("new.title")}</SheetPageTitle>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
        <FormField
          label={t("change_wishlists:form.wishlist_type")}
          name="wishlistItem._create"
          control={control}
          errors={errors}
        >
          <ControlledRadioGroup>
            <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
              <div className="flex flex-row items-center">
                <RadioGroupItem
                  value="true"
                  id="new-wishlist-item"
                  className="peer"
                  aria-labelledby="new-wishlist-item-label"
                  aria-describedby="new-wishlist-item-description"
                />
                <Label
                  htmlFor="new-wishlist-item"
                  className="flex items-center gap-2 rounded-md px-4 py-2"
                >
                  <div className="flex flex-col">
                    <span className="font-medium" id="new-wishlist-item-label">
                      {t("change_wishlists:form.new_wishlist_label")}
                    </span>
                    <span
                      className="text-xs"
                      id="new-wishlist-item-description"
                    >
                      {t("change_wishlists:form.new_wishlist_description")}
                    </span>
                  </div>
                </Label>
              </div>
              <FormField
                name="wishlistItem.student"
                control={control}
                label={t("change_wishlists:form.student")}
                errors={errors}
              >
                <ControlledCombobox
                  options={studentOptions}
                  renderComboboxItem={({ key, setOpen, option }) => (
                    <CommandItem
                      className="flex-col items-start gap-0"
                      key={option.value}
                      disabled={
                        (option as (typeof studentOptions)[number]).disabled
                      }
                      keywords={[key, option.value?.toString() ?? ""]}
                      aria-label={option.label}
                      onSelect={() => {
                        if (wishlistItemStudentField.value === option.value) {
                          wishlistItemStudentField.onChange(null)
                        } else {
                          wishlistItemStudentField.onChange(option.value)
                        }
                        setOpen(false)
                      }}
                    >
                      <span>{option.label}</span>
                    </CommandItem>
                  )}
                />
              </FormField>
              <FormField
                label={t("change_wishlists:form.duration")}
                {...register("wishlistItem.duration", { valueAsNumber: true })}
                errors={errors}
              >
                <Input type="number" placeholder="24" />
              </FormField>
            </div>

            <div className="border-muted has-data-[state=checked]:border-primary has-data-[state=checked]:bg-primary/10 has-data-[state=checked]:hover:bg-primary/15 has-data-[state=unchecked]:hover:bg-brand-dark-grey/10 flex flex-col gap-2 rounded-md border px-4 py-2 transition-colors">
              <div className="flex flex-row items-center">
                <RadioGroupItem
                  value="false"
                  id="exisiting-wishlist"
                  className="peer"
                  aria-labelledby="exisiting-wishlist-label"
                  aria-describedby="exisiting-wishlist-description"
                />
                <Label
                  htmlFor="exisiting-wishlist"
                  className="flex items-center gap-2 rounded-md px-4 py-2"
                >
                  <div className="flex flex-col">
                    <span className="font-medium" id="exisiting-wishlist-label">
                      {t("change_wishlists:form.existing_wishlist_label")}
                    </span>
                    <span
                      className="text-xs"
                      id="exisiting-wishlist-description"
                    >
                      {t("change_wishlists:form.existing_wishlist_description")}
                    </span>
                  </div>
                </Label>
              </div>
              <FormField
                name="wishlistItem.wishlistId"
                control={control}
                errors={errors}
                label={t("change_wishlists:form.wishlist")}
              >
                <ControlledCombobox
                  options={wishlistItemOptions}
                  renderComboboxItem={({ key, setOpen, option }) => (
                    <CommandItem
                      className="flex-col items-start gap-0"
                      key={option.value}
                      disabled={
                        (option as (typeof wishlistItemOptions)[number])
                          .disabled
                      }
                      keywords={[key, option.value?.toString() ?? ""]}
                      aria-label={option.label}
                      onSelect={() => {
                        if (wishlistField.value === option.value) {
                          wishlistField.onChange(null)
                        } else {
                          wishlistField.onChange(option.value)
                        }
                        setOpen(false)
                      }}
                    >
                      <span>{option.label}</span>
                    </CommandItem>
                  )}
                />
              </FormField>
            </div>
          </ControlledRadioGroup>
        </FormField>
        <Button type="submit">{t("common:actions.submit")}</Button>
      </form>
    </SheetPageBody>
  )
}
